import { memo, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'preact-iso';
import MojitoServices from 'mojito/services';
import MojitoPresentation from 'mojito/presentation';
import { omit } from 'mojito/utils';

import DeepLinkingRoutingController from './deep-linking-routing-controller.js';
import { useDispatch, useSelector } from 'react-redux';

const {
    actions: betslipActions,
    selectors: { selectInitialized },
} = MojitoServices.Betslip;
const { IntentActionTrigger } = MojitoPresentation.Base.Intent;

const PARAMS = {
    BET: 'bet_ref',
    CLEAR: 'clear_slip',
    STAKE: 'acc_stake',
    NAV: 'no_nav',
    SOURCE: 'source',
    AFFILIATE: 'affiliate',
    ADVERTISER: 'advertiser',
};

/**
 * Functional React component responsible for handling deep linking navigation that adds selections to betslip.
 *
 * @class DeepLinkingController
 *
 * @returns {object} React route renderer.
 * @memberof Mojito.Applications.Sports
 */
export default memo(function DeepLinkingController() {
    const { url, path, route } = useLocation();
    const search = useMemo(() => new URLSearchParams(url.replace(path, '')), [url, path]);
    const refs = search.getAll(PARAMS.BET);
    const clearSlip = search.get(PARAMS.CLEAR);
    const accStake = search.get(PARAMS.STAKE);
    const noNav = search.get(PARAMS.NAV);
    const source = search.get(PARAMS.SOURCE);
    const affiliate = search.get(PARAMS.ADVERTISER) || search.get(PARAMS.AFFILIATE);
    const dispatch = useDispatch();
    const betslipInitialized = useSelector(selectInitialized);
    // Store skip navigation in ref to not lose its value after cleaning deep linking params with omitDeepLinkingParams.
    // It is safe because DeepLinkingController will be unmounted after deep linking handling. The new instance will be mounted on next deep linking navigation.
    const skipNavigationRef = useRef(noNav === 'true');

    useEffect(() => {
        if (!betslipInitialized) {
            return;
        }
        if (refs.length > 0) {
            const shouldClearBetslip = clearSlip === 'Y';

            const parsedStake = parseFloat(accStake);
            const stake = isFinite(parsedStake) ? parsedStake : 0;
            dispatch(
                betslipActions.executeDeepLinkingActions({
                    refs,
                    stake,
                    clear: shouldClearBetslip,
                    source,
                    affiliate,
                })
            );
        } else {
            IntentActionTrigger.showHomePage(true);
        }
        const cleanParams = omitDeepLinkingParams(search);
        const queryString = new URLSearchParams(cleanParams).toString();
        route(queryString ? `${path}?${queryString}` : path, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [betslipInitialized]);
    return <DeepLinkingRoutingController skipNavigation={skipNavigationRef.current} />;
});

function omitDeepLinkingParams(params) {
    const paramsObject = Object.fromEntries(params.entries());
    return omit(paramsObject, Object.values(PARAMS));
}
